/* BeanBurds house tile behaviour (snippets/bb-equip-card.liquid). 2 Oct 2026.
   Loaded on every page from layout/theme.liquid as a module.

   Add goes through the same path as the product page: POST cart/add with the
   cart sections, wrapped in the theme's CartLinesUpdateEvent. So the cart
   drawer opens and re-renders, the header count updates, and anything else
   listening (the free-beans gift line in snippets/bb-gwp.liquid) reacts
   exactly as it does after adding from a product page.

   A product with more than one buyable variant never adds silently: Add opens
   a picker listing every colour or option with its price and stock.

   Coffee tiles shown outside the coffee pages (search, shelves, brand pages)
   get the same Add path here. On the coffee pages bb-coffee.js owns them; it
   sets window.__bbcInit, and this file stands back when it sees that. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const AR = (document.documentElement.lang || '').toLowerCase().startsWith('ar');
const T = AR
  ? { added: 'أُضيف إلى السلة', pick: 'اختر', close: 'إغلاق', sold: 'نفدت الكمية', err: 'تعذّرت الإضافة. حاول مرة أخرى.', loading: 'جارٍ التحميل…', pre: 'طلب مسبق' }
  : { added: 'added to your cart', pick: 'Choose one to add', close: 'Close', sold: 'Sold out', err: 'Could not add that. Please try again.', loading: 'Loading…', pre: 'Pre-order' };

const routes = () => (window.Theme && window.Theme.routes) || {};
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const addUrl = () => routes().cart_add_url || `${root()}cart/add`;
const cartUrl = () => routes().cart_url || `${root()}cart`;

function cartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el.dataset && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  return ids;
}

let toastTimer;
function toast(msg) {
  let t = document.getElementById('bbt-toast');
  if (!t) {
    t = document.createElement('div');
    t.id = 'bbt-toast';
    t.className = 'bbt-toast';
    t.setAttribute('role', 'status');
    t.setAttribute('aria-live', 'polite');
    document.body.appendChild(t);
  }
  t.textContent = msg;
  requestAnimationFrame(() => t.classList.add('is-show'));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => t.classList.remove('is-show'), 2600);
}

function drawerWillOpen() {
  return Boolean(document.querySelector('cart-drawer-component[auto-open]'));
}

async function addToCart(variantId, trigger, name) {
  const btn = trigger;
  if (btn) {
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
  }
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  (btn || document).dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );
  try {
    const res = await fetch(addUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sections.join(',') }),
    });
    const data = await res.json();
    const cart = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    if (data.status) {
      document.dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID', detail: { description: data.description } }));
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'bb-tile', itemCount: 0, didError: true } });
      toast(data.description || data.message || T.err);
      return false;
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'bb-tile', itemCount: 1, sections: data.sections, didError: false },
    });
    if (btn && btn.classList.contains('bbt-add')) {
      btn.classList.add('is-done');
      setTimeout(() => btn.classList.remove('is-done'), 1600);
    }
    if (!drawerWillOpen()) toast(`${name || 'Item'} ${T.added}`);
    return true;
  } catch (error) {
    deferred.reject(error);
    toast(T.err);
    return false;
  } finally {
    if (btn) {
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
    }
  }
}

/* ---------- option picker ---------- */
let sheet;
let lastTrigger;
function money(cents, tile) {
  const whole = Math.floor(cents / 100);
  const fils = cents % 100;
  const n = whole.toLocaleString('en-US') + (fils ? `.${String(fils).padStart(2, '0')}` : '');
  const usesDh = tile && tile.querySelector('.bbt-now .bbt-dh');
  if (usesDh) return `<svg class="bbt-dh" aria-hidden="true"><use href="#bbt-dh"></use></svg>${n}`;
  const cur = tile && tile.querySelector('.bbt-cur');
  return `${cur ? cur.textContent.trim() + ' ' : ''}${n}`;
}
function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function closeSheet() {
  if (!sheet || sheet.hidden) return;
  sheet.classList.remove('is-open');
  setTimeout(() => { sheet.hidden = true; }, 180);
  document.documentElement.style.removeProperty('overflow');
  if (lastTrigger) lastTrigger.focus({ preventScroll: true });
}
function ensureSheet() {
  if (sheet) return sheet;
  sheet = document.createElement('div');
  sheet.className = 'bbt-sheet';
  sheet.hidden = true;
  sheet.innerHTML = '<div class="bbt-sheet-card" role="dialog" aria-modal="true" aria-labelledby="bbt-sheet-title" tabindex="-1"></div>';
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet || e.target.closest('[data-bbt-close]')) closeSheet();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeSheet();
  });
  document.body.appendChild(sheet);
  return sheet;
}
async function openPicker(trigger) {
  const tile = trigger.closest('[data-bbt]');
  const handle = trigger.getAttribute('data-bbt-pick');
  const name = trigger.getAttribute('data-name') || '';
  lastTrigger = trigger;
  const s = ensureSheet();
  const card = s.querySelector('.bbt-sheet-card');
  card.innerHTML = `<p class="bbt-sheet-sub">${T.loading}</p>`;
  s.hidden = false;
  requestAnimationFrame(() => s.classList.add('is-open'));
  document.documentElement.style.overflow = 'hidden';
  let product;
  try {
    product = await fetch(`${root()}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  } catch (e) {
    window.location.href = `${root()}products/${handle}`;
    return;
  }
  const dots = {};
  if (tile) tile.querySelectorAll('.bbt-dot').forEach((d) => { dots[(d.getAttribute('title') || '').toLowerCase()] = d.style.background; });
  const optNames = (product.options || []).map((o) => (typeof o === 'string' ? o : o.name) || '');
  const colourIdx = optNames.findIndex((n) => /colo|finish/i.test(n));
  const img = product.featured_image ? `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=160` : '';
  const rows = product.variants.map((v) => {
    const cval = colourIdx > -1 ? v.options[colourIdx] : '';
    const dot = cval && dots[cval.toLowerCase()] ? `<span class="bbt-opt-dot" style="background:${esc(dots[cval.toLowerCase()])}"></span>` : '';
    const pre = v.available && v.inventory_management === 'shopify' && v.inventory_policy === 'continue' && v.inventory_quantity <= 0;
    const note = !v.available ? T.sold : pre ? T.pre : '';
    const was = v.compare_at_price && v.compare_at_price > v.price ? ` <s style="font-weight:600;font-size:12px;color:#6B706C">${money(v.compare_at_price, tile)}</s>` : '';
    return `<li><button type="button" class="bbt-opt" data-bbt-opt="${v.id}"${v.available ? '' : ' disabled'}>${dot}<span class="bbt-opt-name">${esc(v.public_title || v.title)}${note ? `<span class="bbt-opt-note">${note}</span>` : ''}</span><span class="bbt-opt-price">${money(v.price, tile)}${was}</span></button></li>`;
  });
  card.innerHTML = `
    <div class="bbt-sheet-head">
      ${img ? `<img class="bbt-sheet-img" src="${esc(img)}" alt="" width="64" height="64">` : ''}
      <div><p class="bbt-sheet-title" id="bbt-sheet-title">${esc(name || product.title)}</p><p class="bbt-sheet-sub">${T.pick}</p></div>
      <button type="button" class="bbt-sheet-close" data-bbt-close aria-label="${T.close}">&times;</button>
    </div>
    <ul class="bbt-opts">${rows.join('')}</ul>
    <p class="bbt-sheet-msg" hidden></p>`;
  card.querySelectorAll('[data-bbt-opt]').forEach((b) => {
    b.addEventListener('click', async () => {
      const ok = await addToCart(b.getAttribute('data-bbt-opt'), b, name || product.title);
      if (ok) closeSheet();
    });
  });
  const first = card.querySelector('.bbt-opt:not([disabled])') || card.querySelector('[data-bbt-close]');
  if (first) first.focus({ preventScroll: true });
}

/* ---------- quote ---------- */
function quote(trigger, e) {
  const form = document.getElementById('bbe-lead');
  if (!form) return; /* no quote form on this page: follow the link to the product */
  e.preventDefault();
  const name = trigger.getAttribute('data-bbt-quote') || '';
  const field = form.querySelector('textarea, [name="contact[body]"], [name="message"]');
  if (field && !field.value) field.value = (window.BBT && window.BBT.quote_request) ? window.BBT.quote_request.split('{name}').join(name) : `Quote request: ${name}`;
  form.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const focusable = form.querySelector('input, select, textarea');
  if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), 400);
}

/* ---------- coffee tiles outside the coffee pages ---------- */
const FKEY = 'bbc-favs';
function favSet() {
  try { return new Set(JSON.parse(localStorage.getItem(FKEY) || '[]')); } catch (e) { return new Set(); }
}
function paintFavs() {
  if (window.__bbcInit) return;
  const f = favSet();
  document.querySelectorAll('.bbc-scope [data-bbc-fav]').forEach((b) => {
    const on = f.has(String(b.getAttribute('data-bbc-fav')));
    b.classList.toggle('active', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
}

document.addEventListener('click', (e) => {
  const t = e.target;
  if (!t || !t.closest) return;
  const add = t.closest('[data-bbt-add]');
  if (add) {
    e.preventDefault();
    if (!add.disabled) addToCart(add.getAttribute('data-bbt-add'), add, add.getAttribute('data-name'));
    return;
  }
  const pick = t.closest('[data-bbt-pick]');
  if (pick) {
    e.preventDefault();
    openPicker(pick);
    return;
  }
  const q = t.closest('[data-bbt-quote]');
  if (q) {
    quote(q, e);
    return;
  }
  if (window.__bbcInit) return;
  const cAdd = t.closest('.bbc-scope [data-bbc-add]');
  if (cAdd) {
    e.preventDefault();
    if (!cAdd.disabled) addToCart(cAdd.getAttribute('data-bbc-add'), cAdd, cAdd.getAttribute('data-name'));
    return;
  }
  const fav = t.closest('.bbc-scope [data-bbc-fav]');
  if (fav) {
    e.preventDefault();
    const id = String(fav.getAttribute('data-bbc-fav'));
    const f = favSet();
    const on = !f.has(id);
    if (on) f.add(id); else f.delete(id);
    try { localStorage.setItem(FKEY, JSON.stringify(Array.from(f))); } catch (x) { /* private mode */ }
    fav.classList.toggle('active', on);
    fav.setAttribute('aria-pressed', on ? 'true' : 'false');
  }
});

paintFavs();
document.addEventListener('shopify:section:load', paintFavs);
